/**
 * SEIRD — adds a Death compartment to SEIR.
 *
 * Mortality is partitioned out of the recovery flux by IFR (infection
 * fatality ratio):
 *
 *   dS/dt = -β·S·I / N
 *   dE/dt =  β·S·I / N - σ·E
 *   dI/dt =  σ·E - γ·I
 *   dR/dt =  γ·(1 - IFR)·I
 *   dD/dt =  γ·IFR·I
 *
 * This treats death and recovery as happening at the same rate γ (the
 * "outcome rate"), with IFR controlling the split. More realistic
 * SEIRD variants use separate γ_R and γ_D; this is v0.2+.
 */

import { integrateRk4 } from './rk4.js';
import { ENGINE_VERSION } from './sir.js';
import type { SeirdParams, EpiResult, BetaScaler } from './types.js';

export interface SeirdOptions {
  betaScaler?: BetaScaler;
}

export function simulateSeird(params: SeirdParams, opts: SeirdOptions = {}): EpiResult {
  const N = params.N;
  const I0 = params.I0;
  const E0 = params.E0 ?? 0;
  const R0Pop = params.R0Pop ?? 0;
  const S0 = N - I0 - E0 - R0Pop;
  const dt = params.dtDays ?? 0.5;
  const beta = params.beta;
  const gamma = params.gamma;
  const sigma = params.sigma;
  const ifr = params.ifr;
  const scaler = opts.betaScaler;
  const t0 = performance.now();

  if (S0 < 0) throw new Error(`SEIRD: N < I0+E0+R0Pop`);
  if (ifr < 0 || ifr > 1) throw new Error(`SEIRD: ifr (${ifr}) must be in [0,1]`);
  if (beta < 0) throw new Error(`SEIRD: beta must be non-negative`);
  if (gamma <= 0 || sigma <= 0) throw new Error(`SEIRD: gamma and sigma must be positive`);

  const f = (t: number, y: number[]): number[] => {
    const S = y[0]!, E = y[1]!, I = y[2]!;
    const b = scaler ? beta * scaler(t) : beta;
    const newExposed = (b * S * I) / N;
    const becomeInfectious = sigma * E;
    const outcomes = gamma * I;
    return [
      -newExposed,
      newExposed - becomeInfectious,
      becomeInfectious - outcomes,
      outcomes * (1 - ifr),
      outcomes * ifr,
    ];
  };

  const integrated = integrateRk4(f, [S0, E0, I0, R0Pop, 0], params.durationDays, dt);
  const compartments: Record<string, number[]> = { S: [], E: [], I: [], R: [], D: [] };
  for (const row of integrated.y) {
    compartments.S!.push(row[0]!);
    compartments.E!.push(row[1]!);
    compartments.I!.push(row[2]!);
    compartments.R!.push(row[3]!);
    compartments.D!.push(row[4]!);
  }

  return {
    t: integrated.t,
    compartments,
    receipt: {
      engineVersion: ENGINE_VERSION,
      seed: 'seird',
      elapsedMs: performance.now() - t0,
      steps: integrated.steps,
    },
  };
}
